'use client'

import type { DeviceType } from '@/app/util/env'
import UserContext from '@/context/user-context'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import { useContext, useEffect, useRef, useState } from 'react'
import { getUserExperiences } from './api'
import {
    UserExperience,
    findUpgradedOnboardingExperience,
    getPendingUpgradedOnboardingExperience,
    hasUpgradedOnboardingHash,
    isCompletedExperience,
    shouldAutoOpenOnboarding,
    shouldFetchExperiences,
    shouldMountUpgradedOnboarding,
    shouldOpenHashOnboarding,
} from './experience'
import type { UpgradedUserOnboardingModalProps } from './modal'

const DynamicUpgradedUserOnboardingModal = dynamic<UpgradedUserOnboardingModalProps>(() => import('./modal'), {
    loading: () => null,
    ssr: false,
})

type Props = {
    deviceType: DeviceType
}

const getCurrentHash = () => (typeof window === 'undefined' ? '' : window.location.hash)

const UpgradedUserOnboardingGate = ({ deviceType }: Props) => {
    const { user, hasFetchedUser } = useContext(UserContext)
    const pathname = usePathname() || ''
    const [hash, setHash] = useState(getCurrentHash)
    const [experience, setExperience] = useState<UserExperience | null>(null)
    const requestKeyRef = useRef<string | null>(null)
    const shouldMount = shouldMountUpgradedOnboarding(deviceType)

    useEffect(() => {
        if (!shouldMount) {
            return
        }

        const updateHash = () => setHash(getCurrentHash())
        updateHash()
        window.addEventListener('hashchange', updateHash)

        return () => {
            window.removeEventListener('hashchange', updateHash)
        }
    }, [shouldMount])

    useEffect(() => {
        if (!shouldMount || !hasFetchedUser || !user) {
            return
        }

        const hasHash = hasUpgradedOnboardingHash(hash)
        const shouldOpenHash = shouldOpenHashOnboarding({ deviceType, user, hasHash })
        if (shouldOpenHash) {
            setExperience(getPendingUpgradedOnboardingExperience())
        }

        if (!shouldFetchExperiences({ deviceType, user, hasHash, pathname })) {
            return
        }

        const requestKey = `${user.id || user.username}:${pathname}:${hasHash ? hash : 'auto'}`
        if (requestKeyRef.current === requestKey) {
            return
        }
        requestKeyRef.current = requestKey

        let isCurrentRequest = true
        getUserExperiences()
            .then((experiences) => {
                if (!isCurrentRequest) {
                    return
                }

                const upgradedExperience = findUpgradedOnboardingExperience(experiences)
                if (hasHash) {
                    if (upgradedExperience) {
                        setExperience(upgradedExperience)
                    }
                    return
                }

                if (
                    upgradedExperience &&
                    shouldAutoOpenOnboarding({
                        deviceType,
                        user,
                        experience: upgradedExperience,
                        pathname,
                    })
                ) {
                    setExperience(upgradedExperience)
                }
            })
            .catch(() => {
                if (isCurrentRequest && requestKeyRef.current === requestKey) {
                    requestKeyRef.current = null
                }
            })

        return () => {
            isCurrentRequest = false
            if (requestKeyRef.current === requestKey) {
                requestKeyRef.current = null
            }
        }
    }, [deviceType, hasFetchedUser, hash, pathname, shouldMount, user])

    if (!shouldMount || !experience || !user) {
        return null
    }

    return (
        <DynamicUpgradedUserOnboardingModal
            initialExperience={experience}
            initialIsCompleted={isCompletedExperience(experience)}
            onClose={() => setExperience(null)}
            user={user}
        />
    )
}

export default UpgradedUserOnboardingGate
